import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";

const WHATSAPP = "918839790836";

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const paragraphs = [
  "A while back, I started noticing something that didn't sit right with me. We're living in a time where almost everything gets simpler with an app — a couple of taps and your food's ordered, your cab's booked, your movie ticket's confirmed. But step into how most small businesses actually run day to day, especially ones like farmhouses and rental spaces, and it feels like time has stood still. A register for bookings. A diary to track who paid what. Someone's memory doing the job of a system, trying to remember if this Saturday is even free.",
  "That gap stayed with me. Not because pen and paper is wrong — it's worked for a long time, and a lot of owners have built real businesses on it. But it doesn't scale, it's easy to lose a page or a number, and it makes it genuinely hard to step back and actually see how your business is doing.",
  "That's why Pulse exists.",
  "The idea is simple: bring the same ease we've gotten used to in our personal lives into how small businesses are run. We're starting with something every rental and farmhouse owner deals with constantly — bookings and calendars — and building it in a way that also shows you your revenue, your busiest months, and your business at a glance, without needing to be technical or hire someone just to manage it.",
  "Pulse isn't here to replace how you run your business. It's here to take the parts that eat up your time and headspace — remembering, tracking, calculating — and make them simpler, so you can spend more of your energy on the part you actually care about: growing it.",
  "This is still early days for Pulse, and the calendar is just the beginning of what I want to build. But if you're running a business and you're tired of doing all of this on paper, I'd genuinely love for you to try Pulse out — and grow with us.",
];

export default function AboutUs() {
  return (
    <div className="bg-white" data-testid="about-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-pulse-navy text-white grain-overlay">
        <div className="relative z-10 mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-28">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-pulse-green">About Us</span>
            <h1 className="mt-4 text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
              Why I built Pulse
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/60">
              The heartbeat of your business — built to make running a venue calmer, one calendar at a time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-24">
        <motion.p
          initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
          className="text-lg font-medium text-pulse-navy"
        >
          Hi, I'm Daksh Jhanbia, the founder of Pulse.
        </motion.p>

        <div className="mt-8 space-y-7">
          {paragraphs.map((p, i) => (
            <motion.p
              key={i}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fade}
              className={`leading-[1.85] ${
                p === "That's why Pulse exists."
                  ? "text-xl font-semibold text-pulse-navy"
                  : "text-base text-charcoal/80 sm:text-lg"
              }`}
            >
              {p}
            </motion.p>
          ))}
        </div>

        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
          className="mt-12 border-l-2 border-pulse-green pl-5"
        >
          <div className="text-lg font-semibold italic text-pulse-navy">— Daksh Jhanbia</div>
          <div className="text-sm text-slate">Founder, Pulse</div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-100 bg-cloud/50">
        <div className="mx-auto flex max-w-3xl flex-col items-start gap-4 px-5 py-14 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div>
            <h2 className="text-2xl font-bold text-pulse-navy">Tired of doing it all on paper?</h2>
            <p className="mt-1 text-slate">Try Pulse free for 15 days — and grow with us.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/pricing" data-testid="about-cta-trial"
              className="flex items-center gap-2 rounded-md bg-pulse-navy px-5 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.97]">
              Start free trial <ArrowRight size={16} />
            </Link>
            <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-md border border-slate-200 px-5 py-3 text-sm font-semibold text-pulse-navy transition-colors hover:bg-white">
              <MessageCircle size={16} /> Talk to us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
